import { LitElement, css, html } from 'lit';
import { gsap } from 'gsap';

export class Modal extends LitElement {
  static properties = {
    open: { type: Boolean, reflect: true },
    closeOnOutsideClick: { type: Boolean, attribute: 'close-on-outside-click' },
    closeOnEscape: { type: Boolean, attribute: 'close-on-escape' },
    modalWidth: { type: String, attribute: 'modal-width' },
    modalHeight: { type: String, attribute: 'modal-height' }
  };

  static styles = css`
    :host {
      display: none;
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      z-index: 9999;
    }

    :host([open]) {
      display: block;
    }

    .modal-overlay {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background-color: transparent;
      user-select: none;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 2rem;
    }

    .modal-container {
      position: relative;
      color: var(--foreground);
      background-color: color-mix(in srgb, var(--background), transparent 25%);
      backdrop-filter: blur(12px);
      border: 2px solid var(--foreground);
      border-radius: 30px;
      box-shadow: 25px 25px 19.3px 0px rgba(0, 0, 0, 0.25);
      max-width: 90vw;
      max-height: 90vh;
      overflow-y: auto;
      overflow-x: hidden;
      cursor: default;
    }

    .modal-content {
      padding: 2rem;
      position: relative;
    }

    .modal-close {
      position: absolute;
      top: 1rem;
      right: 1rem;
      z-index: 1;
      background: none;
      border: none;
      color: var(--foreground);
      font-size: 1.5rem;
      cursor: pointer;
      width: 2rem;
      height: 2rem;
      display: flex;
      align-items: center;
      justify-content: center;
    }

    ::slotted(*) {
      position: relative;
    }
  `;

  constructor() {
    super();
    this.open = false;
    this.closeOnOutsideClick = true;
    this.closeOnEscape = true;
    this.modalWidth = '720px';
    this.modalHeight = 'auto';
    this._boundKeyHandler = this._handleKeydown.bind(this);
    this._previouslyFocused = null;
    this._focusableElements = [];
  }

  connectedCallback() {
    super.connectedCallback();
    if (this.closeOnEscape) {
      document.addEventListener('keydown', this._boundKeyHandler);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('keydown', this._boundKeyHandler);
  }

  firstUpdated() {
    this._overlay = this.shadowRoot.querySelector('.modal-overlay');
    this._container = this.shadowRoot.querySelector('.modal-container');

    // Set initial hidden state
    gsap.set([this._overlay, this._container], { opacity: 0 });
    gsap.set(this._container, { scale: 0.9 });
  }

  updated(changedProperties) {
    if (changedProperties.has('open')) {
      if (this.open) {
        this._showModal();
      } else {
        this._hideModal();
      }
    }
  }

  _showModal() {
    // Store previously focused element
    this._previouslyFocused = document.activeElement;

    // Animate modal appearance
    const tl = gsap.timeline();

    tl.to(this._overlay, {
      opacity: 1,
      duration: 0.2,
      ease: 'power2.out'
    })
      .to(this._container, {
        opacity: 1,
        scale: 1,
        duration: 0.3,
        ease: 'power2.out'
      }, '-=0.1');

    // Focus management
    requestAnimationFrame(() => {
      this._trapFocus();
    });
  }

  _hideModal() {
    // Animate modal disappearance
    const tl = gsap.timeline({
      onComplete: () => {
        // Restore focus to previously focused element
        if (this._previouslyFocused && this._previouslyFocused.focus) {
          this._previouslyFocused.focus();
        }
      }
    });

    tl.to(this._container, {
      opacity: 0,
      scale: 0.9,
      duration: 0.2,
      ease: 'power2.in'
    })
      .to(this._overlay, {
        opacity: 0,
        duration: 0.2,
        ease: 'power2.in'
      }, '-=0.1');
  }

  _trapFocus() {
    // Get all focusable elements within the modal
    const focusableSelectors = 'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';
    this._focusableElements = [...this.shadowRoot.querySelectorAll(focusableSelectors)];

    // Also check slotted content
    const slot = this.shadowRoot.querySelector('slot');
    if (slot) {
      const slottedElements = slot.assignedElements({ flatten: true });
      slottedElements.forEach(el => {
        this._focusableElements.push(...el.querySelectorAll(focusableSelectors));
      });
    }

    // Focus first focusable element or the container
    if (this._focusableElements.length > 0) {
      this._focusableElements[0].focus();
    } else {
      this._container.focus();
    }
  }

  _handleKeydown(e) {
    if (!this.open) return;

    if (e.key === 'Escape' && this.closeOnEscape) {
      this.open = false;
      e.preventDefault();
    }

    if (e.key === 'Tab') {
      // Trap tab focus within modal
      if (this._focusableElements.length === 0) return;

      const firstFocusable = this._focusableElements[0];
      const lastFocusable = this._focusableElements[this._focusableElements.length - 1];

      if (e.shiftKey) {
        if (document.activeElement === firstFocusable) {
          lastFocusable.focus();
          e.preventDefault();
        }
      } else {
        if (document.activeElement === lastFocusable) {
          firstFocusable.focus();
          e.preventDefault();
        }
      }
    }
  }

  _handleOverlayClick(e) {
    if (this.closeOnOutsideClick && e.target === this._overlay) {
      this.open = false;
    }
  }

  _handleCloseClick() {
    this.open = false;
  }

  render() {
    const containerStyles = `
      width: ${this.modalWidth};
      height: ${this.modalHeight};
    `;

    return html`
      <div class="modal-overlay" @click=${this._handleOverlayClick}>
        <div
          class="modal-container"
          style="${containerStyles}"
          role="dialog"
          aria-modal="true"
          tabindex="-1"
          @click=${(e) => e.stopPropagation()}
        >
          <button
            class="modal-close"
            @click=${this._handleCloseClick}
            aria-label="Close modal"
          >
            ✕
          </button>
          <div class="modal-content">
            <slot></slot>
          </div>
        </div>
      </div>
    `;
  }
}

customElements.define('gt-modal', Modal);
